import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RequestDetailsModal = ({ isOpen, onClose, request, onAssignManager }) => {
  if (!isOpen || !request) return null;

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-warning/10 text-warning border-warning/20',
      'approved': 'bg-success/10 text-success border-success/20',
      'in-progress': 'bg-primary/10 text-primary border-primary/20',
      'converted': 'bg-secondary/10 text-secondary border-secondary/20',
      'rejected': 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'text-muted-foreground',
      'medium': 'text-primary',
      'high': 'text-warning',
      'critical': 'text-error'
    };
    return colors?.[priority] || 'text-muted-foreground';
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      'low': 'ArrowDown',
      'medium': 'Minus',
      'high': 'ArrowUp',
      'critical': 'AlertTriangle'
    };
    return icons?.[priority] || 'Minus';
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Request Details</h2>
              <p className="text-sm text-muted-foreground">{request?.requestId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(request?.status)}`}>
              {request?.statusLabel}
            </span>
            <div className={`flex items-center gap-2 ${getPriorityColor(request?.priority)}`}>
              <Icon name={getPriorityIcon(request?.priority)} size={18} color="currentColor" />
              <span className="text-sm font-medium capitalize">{request?.priority} Priority</span>
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-6">
            <h3 className="text-sm font-caption font-medium text-muted-foreground mb-4">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Name</p>
                <p className="font-medium text-foreground">{request?.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Email</p>
                <p className="font-medium text-foreground">{request?.customerEmail}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Phone</p>
                <p className="font-medium text-foreground">{request?.customerPhone}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Customer ID</p>
                <p className="font-medium text-foreground font-mono">{request?.customerId}</p>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-6">
            <h3 className="text-sm font-caption font-medium text-muted-foreground mb-4">Service Details</h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Service Type</p>
                <p className="font-medium text-foreground">{request?.serviceType}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2">Products/Services Requested</p>
                <div className="space-y-2">
                  {request?.products?.map((product, index) => (
                    <div key={index} className="flex items-center gap-3 bg-background rounded-lg p-3">
                      <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                        <Icon name="Package" size={16} color="var(--color-primary)" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-foreground text-sm">{product?.name}</p>
                        <p className="text-xs text-muted-foreground">{product?.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Description</p>
                <p className="text-sm text-foreground leading-relaxed">{request?.description}</p>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-caption font-medium text-muted-foreground">Manager Assignment</h3>
              {!request?.assignedManager && onAssignManager && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="UserPlus"
                  iconPosition="left"
                  onClick={() => onAssignManager(request)}
                >
                  Assign Manager
                </Button>
              )}
            </div>
            {request?.assignedManager ? (
              <div className="flex items-center gap-3 bg-background rounded-lg p-4">
                <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="UserCheck" size={20} color="var(--color-secondary)" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">{request?.assignedManager}</p>
                  <p className="text-xs text-muted-foreground">Assigned Manager</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-warning/5 border border-warning/20 rounded-lg p-4">
                <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
                <p className="text-sm text-muted-foreground">No manager assigned yet</p>
              </div>
            )}
          </div>

          <div className="bg-muted/30 rounded-lg p-6">
            <h3 className="text-sm font-caption font-medium text-muted-foreground mb-4">Request Timeline</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Clock" size={16} color="var(--color-success)" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground text-sm">Request Created</p>
                  <p className="text-xs text-muted-foreground">{request?.createdDate} at {request?.createdTime}</p>
                </div>
              </div>
              {request?.lastUpdated && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="RefreshCw" size={16} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground text-sm">Last Updated</p>
                    <p className="text-xs text-muted-foreground">{request?.lastUpdated}</p>
                  </div>
                </div>
              )}
              {request?.assignedManager && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="UserCheck" size={16} color="var(--color-secondary)" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground text-sm">Assigned Manager</p>
                    <p className="text-xs text-muted-foreground">{request?.assignedManager}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {request?.notes && (
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="StickyNote" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Internal Notes</p>
                  <p className="text-sm text-muted-foreground">{request?.notes}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-6">
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
            iconName="X"
            iconPosition="left"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RequestDetailsModal;